import type { TExposedApplicationAggregateDTO } from '@doutorsim/shared/applications'
import type { TPaginationMeta } from '@doutorsim/shared/pagination'
import type { TExposedPersonDTO } from '@doutorsim/shared/exposed/persons'
import type { TApplicationStatus } from '@doutorsim/shared/applications'
import { apiClient } from '../api'
import { type GetApplicationsParams, buildApplicationSearchParams } from './applications'

export type TCompanyApplicationListItem = Pick<
  TExposedApplicationAggregateDTO,
  | 'id'
  | 'companyId'
  | 'facilityId'
  | 'createdAt'
  | 'updatedAt'
  | 'installmentCount'
  | 'proposal'
  | 'guarantors'
> & {
  person: Pick<TExposedPersonDTO, 'name' | 'cpf' | 'email' | 'phone'>
  status: TApplicationStatus
  requestedAmount: number
}

export type TCompanyApplicationsPaginatedResponse = {
  data: TCompanyApplicationListItem[]
  meta: TPaginationMeta
}

function mapApplicationToListItem(
  app: TExposedApplicationAggregateDTO,
): TCompanyApplicationListItem {
  return {
    id: app.id,
    companyId: app.companyId,
    facilityId: app.facilityId,
    createdAt: app.createdAt,
    updatedAt: app.updatedAt,
    person: {
      name: app.person?.name ?? 'Unknown',
      cpf: app.person?.cpf ?? '',
      email: app.person?.email ?? '',
      phone: app.person?.phone ?? '',
    },
    guarantors: app.guarantors ?? [],
    status: app.status || 'pending-analysis',
    // Kept in cents (matches the API DTO and proposal amounts); formatted at the
    // display boundary with formatBRLFromCents.
    requestedAmount: app.requestedAmount ?? 0,
    installmentCount: app.installmentCount,
    proposal: app.proposal,
  }
}

class CompanyApplicationsApi {
  async getApplications(
    companyId: string,
    params?: GetApplicationsParams,
  ): Promise<TCompanyApplicationsPaginatedResponse> {
    const baseUrl = `/api/v1/companies/${companyId}/applications`
    const queryParams = buildApplicationSearchParams(params)
    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl
    const { data: applications, meta } = await apiClient.get<TExposedApplicationAggregateDTO[]>(url)

    if (!applications) return { data: [], meta: { hasMore: false } }

    return {
      data: applications.map(mapApplicationToListItem),
      meta: meta ?? { hasMore: false },
    }
  }
}

export const companyApplicationsApi = new CompanyApplicationsApi()
